import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, MailCheck } from 'lucide-react';
import { authAPI } from '../services/api';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token') || '';
  const isResetMode = useMemo(() => !!tokenFromUrl, [tokenFromUrl]);

  const [email, setEmail] = useState('');
  const [token] = useState(tokenFromUrl);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const passwordsDoNotMatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  const handleForgotSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await authAPI.forgotPassword(email);
      toast.success(response.data?.message || 'Se o email existir, enviaremos um link de redefinição.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível processar sua solicitação.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async (event) => {
    event.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('As senhas não conferem. Confira a digitação e tente novamente.');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.resetPassword(token, newPassword);
      toast.success(response.data?.message || 'Senha redefinida com sucesso.');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível redefinir sua senha.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen login-bg flex items-center justify-center p-4">
      <div className="grid-overlay" />

      <div className="w-full max-w-md z-10 slide-up">
        <div className="flex justify-center mb-8">
          <Logo size="lg" />
        </div>

        <Card className="glass-card border-white/10">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-white">
              {isResetMode ? 'Redefinir senha' : 'Esqueci minha senha'}
            </CardTitle>
            <CardDescription className="text-neutral-500">
              {isResetMode
                ? 'Crie uma nova senha para sua conta.'
                : 'Digite seu email para receber um link seguro de redefinição.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isResetMode ? (
              <form onSubmit={handleResetSubmit} className="space-y-4">
                <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-3 text-sm text-amber-100 flex gap-3">
                  <MailCheck className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>Link validado. Informe sua nova senha abaixo.</span>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-password" className="text-neutral-500 uppercase text-xs tracking-wide">Nova senha</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    placeholder="mín. 10 caracteres com letras e número"
                    required
                    className="bg-neutral-900/50 border-neutral-800 text-white placeholder:text-neutral-600 h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="text-neutral-500 uppercase text-xs tracking-wide">Confirmar senha</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    placeholder="digite a nova senha novamente"
                    required
                    aria-invalid={passwordsDoNotMatch}
                    aria-describedby={passwordsDoNotMatch ? 'confirm-password-error' : undefined}
                    className="bg-neutral-900/50 border-neutral-800 text-white placeholder:text-neutral-600 h-12"
                  />
                  {passwordsDoNotMatch ? (
                    <p id="confirm-password-error" className="text-xs text-red-400">
                      As senhas não conferem.
                    </p>
                  ) : null}
                </div>
                <Button type="submit" disabled={loading || passwordsDoNotMatch} className="w-full h-12 btn-primary">
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Redefinir senha
                </Button>
              </form>
            ) : (
              <form onSubmit={handleForgotSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-neutral-500 uppercase text-xs tracking-wide">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                    className="bg-neutral-900/50 border-neutral-800 text-white placeholder:text-neutral-600 h-12"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-12 btn-primary">
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Enviar instruções
                </Button>
              </form>
            )}

            <Button
              variant="ghost"
              className="w-full mt-4 text-neutral-400 hover:text-white"
              onClick={() => navigate('/')}
            >
              <ArrowLeft className="mr-2" size={16} />
              Voltar para o login
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
